import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Skeleton,
    Avatar,
    IconButton,
    InputAdornment,
    Button,
    Grid
} from '@mui/material';
import { Search, Person, Storefront, Visibility, Block, Delete, Edit } from '@mui/icons-material';
import { useAdminAllVendorStaff, useUpdateVendorStaffStatus, useAdminUpdateVendorStaff, useAdminDeleteVendorStaff } from 'hooks/useStaff';
import { useAdminAllVendors } from 'hooks/useVendors';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormGroup,
    FormControlLabel,
    Checkbox,
    CircularProgress
} from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function AdminStaffList() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [selectedVendor, setSelectedVendor] = useState('');
    const [editingStaff, setEditingStaff] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const { data: vendorData } = useAdminAllVendors({ limit: 100 });
    const vendors = vendorData?.data || [];

    const {
        data: staffData,
        isLoading,
        refetch
    } = useAdminAllVendorStaff({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        vendorId: selectedVendor || undefined
    });

    const updateStatusMutation = useUpdateVendorStaffStatus();
    const updateMutation = useAdminUpdateVendorStaff();
    const deleteMutation = useAdminDeleteVendorStaff();

    const staffs = staffData?.data || [];
    const totalStaffs = staffData?.count || 0;

    const handleToggleStatus = (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
        if (window.confirm(`Are you sure you want to ${newStatus} this staff member?`)) {
            updateStatusMutation.mutate(
                { id, status: newStatus },
                {
                    onSuccess: () => refetch()
                }
            );
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) {
            try {
                await deleteMutation.mutateAsync(id);
                toast.success('Staff deleted successfully');
                refetch();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleEdit = (staff) => {
        setEditingStaff(staff);
        setModalOpen(true);
    };

    return (
        <Box p={3}>
            <Typography variant="h4" fontWeight={700} mb={4}>
                Vendor Staff Oversight
            </Typography>

            <Paper sx={{ width: '100%', borderRadius: 3, p: 2 }}>
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <TextField
                        placeholder="Search by name, email or store..."
                        size="small"
                        sx={{ width: 400 }}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="vendor-filter-label">Filter by Vendor</InputLabel>
                        <Select
                            labelId="vendor-filter-label"
                            value={selectedVendor}
                            label="Filter by Vendor"
                            onChange={(e) => {
                                setSelectedVendor(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">All Vendors</MenuItem>
                            {vendors.map((vendor) => (
                                <MenuItem key={vendor.id} value={vendor.id}>
                                    {vendor.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Staff Member</TableCell>
                                <TableCell>Vendor Store</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading
                                ? [...Array(5)].map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell colSpan={5}>
                                              <Skeleton height={50} />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : staffs.map((staff) => (
                                      <TableRow key={staff.id} hover>
                                          <TableCell>
                                              <Stack direction="row" spacing={2} alignItems="center">
                                                  <Avatar sx={{ bgcolor: 'secondary.light' }}>
                                                      <Person fontSize="small" />
                                                  </Avatar>
                                                  <Box>
                                                      <Typography variant="subtitle2" fontWeight={600}>
                                                          {staff.user?.firstName} {staff.user?.lastName}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                          {staff.user?.email}
                                                      </Typography>
                                                  </Box>
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Stack direction="row" spacing={1} alignItems="center">
                                                  <Storefront fontSize="inherit" color="action" />
                                                  <Typography variant="body2">{staff.vendor?.name}</Typography>
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Chip label={staff.role} size="small" variant="outlined" />
                                          </TableCell>
                                          <TableCell>
                                              <Chip
                                                  label={staff.status}
                                                  size="small"
                                                  color={staff.status === 'active' ? 'success' : 'error'}
                                              />
                                          </TableCell>
                                          <TableCell align="right">
                                              <IconButton color="primary" onClick={() => handleEdit(staff)}>
                                                  <Edit fontSize="small" />
                                              </IconButton>
                                              <IconButton
                                                  color={staff.status === 'active' ? 'error' : 'success'}
                                                  onClick={() => handleToggleStatus(staff.id, staff.status)}
                                                  disabled={updateStatusMutation.isPending}
                                                  title={staff.status === 'active' ? 'Suspend' : 'Activate'}
                                              >
                                                  {updateStatusMutation.isPending ? (
                                                      <CircularProgress size={20} />
                                                  ) : (
                                                      <Block fontSize="small" />
                                                  )}
                                              </IconButton>
                                              <IconButton color="error" onClick={() => handleDelete(staff.id)} disabled={deleteMutation.isPending}>
                                                  <Delete fontSize="small" />
                                              </IconButton>
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalStaffs}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
            </Paper>

            <AdminEditStaffModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                staff={editingStaff}
                updateMutation={updateMutation}
                onSuccess={() => refetch()}
            />
            <ToastContainer position="top-right" autoHideDuration={5000} />
        </Box>
    );
}

const STAFF_PERMISSIONS = [
    { value: 'manage_inventory', label: 'Manage Inventory' },
    { value: 'manage_orders', label: 'Manage Orders' },
    { value: 'manage_staff', label: 'Manage Staff' },
    { value: 'manage_finance', label: 'Manage Finance' },
    { value: 'view_analytics', label: 'View Analytics' }
];

function AdminEditStaffModal({ open, onClose, staff, updateMutation, onSuccess }) {
    const [formData, setFormData] = React.useState({
        role: 'staff',
        permissions: []
    });

    React.useEffect(() => {
        if (staff) {
            setFormData({
                role: staff.role || 'staff',
                permissions: staff.permissions || []
            });
        }
    }, [staff, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (perm) => {
        setFormData((prev) => {
            const has = prev.permissions.includes(perm);
            return {
                ...prev,
                permissions: has ? prev.permissions.filter((p) => p !== perm) : [...prev.permissions, perm]
            };
        });
    };

    const handleSubmit = async () => {
        try {
            await updateMutation.mutateAsync({ id: staff.id, payload: formData });
            toast.success('Staff permissions updated');
            onSuccess();
            onClose();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Staff Permissions</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} pt={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Updating: {staff?.user?.firstName} {staff?.user?.lastName}
                        <br />
                        Store: {staff?.vendor?.name}
                    </Typography>

                    <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select name="role" value={formData.role} label="Role" onChange={handleChange}>
                            <MenuItem value="staff">Staff</MenuItem>
                            <MenuItem value="manager">Manager</MenuItem>
                        </Select>
                    </FormControl>

                    <Box>
                        <Typography variant="subtitle2" mb={1}>
                            Permissions
                        </Typography>
                        <FormGroup>
                            <Grid container spacing={1}>
                                {STAFF_PERMISSIONS.map((p) => (
                                    <Grid item xs={12} key={p.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.permissions.includes(p.value)}
                                                    onChange={() => handlePermissionChange(p.value)}
                                                />
                                            }
                                            label={p.label}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </FormGroup>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
